import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { UserService } from '../../services/user.service';

// Thunks
import { openNotification } from '../app-notifications/app-notifications.thunks';

// Slices
import { UserSliceActions } from './user.slice';

// Models
import { INotificationSettings, IUpdateUserData, IUserData } from '../../interfaces/user.interfaces';
import { ICounsellorCreate } from '../../interfaces/counsellors.interfaces';

export enum EUserProfileThunks {
  GetProfile = 'USER/getProfile',
  SaveUserProfile = 'USER/saveUserProfile',
  GetNotificationSettings = 'USER/getNotificationSettings',
  UpdateProfile = 'USER/updateProfile',
  UpdateProfileEmail = 'USER/updateProfileEmail',
  MakeMeACounsellor = 'USER/makeMeACounsellor',
  NotificationSettingsUpdate = 'USER/notificationSettingsUpdate',
  DeleteProfile = 'USER/deleteProfile',
  UserPhotoUpdate = 'USER/userPhotoUpdate',
  UserPhotoDelete = 'USER/userPhotoDelete',
  UserLogout = 'USER/userLogout',
  UserDeleteLogout = 'USER/userDeleteLogout',
  UpdateLastCompany = 'USER/updateLastCompany',
  UserLogoutNotValidToken = 'USER/userLogoutNotValidToken',
}

export const getUserProfile = createAsyncThunk(
  EUserProfileThunks.GetProfile,
  async (_, { dispatch }) => {
    dispatch(UserSliceActions.profileRequest());
    const res = await UserService.profile();
    if (res.response && res.response.status !== 200) {
      dispatch(UserSliceActions.profileFailure(res.response.data.error));
      return;
    }
    dispatch(UserSliceActions.profileSuccess(res.data?.worker_profile));
    return res.data;
  }
);

export const saveUserProfile = createAsyncThunk(
  EUserProfileThunks.SaveUserProfile,
  async (user: IUserData, { dispatch }: any) => {
    dispatch(UserSliceActions.profileSuccess(user));
  }
);

export const getUserNotificationSettings = createAsyncThunk(
  EUserProfileThunks.GetNotificationSettings,
  async (_, { dispatch }) => {
    dispatch(UserSliceActions.profileRequest());
    const { notification_settings, error } = await UserService.notificationSettings();
    if (!notification_settings) {
      dispatch(UserSliceActions.profileFailure(error ?? 'Error'));
      return;
    }
    dispatch(UserSliceActions.notificationSettingsUpdate(notification_settings));
    return notification_settings;
  }
);

export const updateProfile = createAsyncThunk(
  EUserProfileThunks.UpdateProfile,
  async (user: IUpdateUserData, { dispatch }) => {
    dispatch(UserSliceActions.profileUpdateRequest());
    const { worker_profile, error, message } = await UserService.updateProfile(user);
    if (!worker_profile) {
      dispatch(UserSliceActions.profileUpdateFailure());
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update info, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(UserSliceActions.profileUpdateSuccess(worker_profile));
    return worker_profile;
  }
);

export const updateProfileEmail = createAsyncThunk(
  EUserProfileThunks.UpdateProfileEmail,
  async (email: string, { dispatch }) => {
    dispatch(UserSliceActions.updateProfileEmail(email));
    return email;
  }
);

export const makeMeACounsellor = createAsyncThunk(
  EUserProfileThunks.MakeMeACounsellor,
  async (counsellorCreate: ICounsellorCreate, { dispatch }) => {
    dispatch(UserSliceActions.profileUpdateRequest());
    const { is_counsellor, error, message } = await UserService.makeMeACounsellor(counsellorCreate);
    if (error) {
      dispatch(UserSliceActions.profileUpdateFailure());
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update profile status, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(UserSliceActions.counsellorStatusUpdate(!!is_counsellor));
    return is_counsellor;
  }
);

export const notificationSettingsUpdate = createAsyncThunk(
  EUserProfileThunks.NotificationSettingsUpdate,
  async (settings: INotificationSettings, { dispatch }) => {
    dispatch(UserSliceActions.profileUpdateRequest());
    const { notification_settings, error } = await UserService.notificationSettingsUpdate(settings);
    if (!notification_settings) {
      dispatch(UserSliceActions.profileUpdateFailure());
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update profile notification settings, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(UserSliceActions.notificationSettingsUpdate(notification_settings));
    return notification_settings;
  }
);

export const deleteProfile = createAsyncThunk(
  EUserProfileThunks.DeleteProfile,
  async (_, { dispatch }) => {
    dispatch(UserSliceActions.profileDeleteRequest());
    const res = await UserService.deleteProfile();
    if (res.response && res.response.status !== 200) {
      dispatch(UserSliceActions.profileDeleteFailure());
      return res.response.data;
    }
    dispatch(UserSliceActions.profileDeleteSuccess());
    return res.data;
  }
);

export const userPhotoUpdate = createAsyncThunk(
  EUserProfileThunks.UserPhotoUpdate,
  async ({ file }: { file: File }, { dispatch }) => {
    dispatch(UserSliceActions.updatePhotoRequest());
    const { error, photo, message } = await UserService.updatePhoto(file);
    if (!photo) {
      dispatch(UserSliceActions.updatePhotoFailure());
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant photo, try again',
        type: 'error'
      }));
      return false;
    }

    dispatch(openNotification({
      description: message ?? 'Photo was successfully saved',
      type: 'success'
    }));
    dispatch(UserSliceActions.updatePhotoSuccess({ photo }));
    return photo;
  }
);

export const userPhotoDelete = createAsyncThunk(
  EUserProfileThunks.UserPhotoDelete,
  async (_, { dispatch }) => {
    dispatch(UserSliceActions.deletePhotoRequest());
    const { error, message } = await UserService.deletePhoto();
    if (error) {
      dispatch(UserSliceActions.deletePhotoFailure());
      dispatch(openNotification({
        description: error ?? 'Couldn\'t delete the consultant photo, try again',
        type: 'error'
      }));
      return false;
    }

    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));

    dispatch(UserSliceActions.deletePhotoSuccess());
    return true;
  }
);

export const userLogout = createAsyncThunk(
  EUserProfileThunks.UserLogout,
  async (_, { dispatch }: any) => {
    dispatch(UserSliceActions.logoutRequest());
    const res = await UserService.logout();
    if (res.response && res.response.status !== 200) {
      dispatch(UserSliceActions.logoutFailure());
      return res.response.data;
    }
    localStorage.clear();
    dispatch(UserSliceActions.logoutSuccess());
    return res.data;
  }
);

export const userDeleteLogout = createAsyncThunk(
  EUserProfileThunks.UserDeleteLogout,
  async (_, { dispatch }: any) => {
    localStorage.clear();
    dispatch(UserSliceActions.deleteLogoutRequest());
  }
);

export const userLogoutNotValidToken = createAsyncThunk(
  EUserProfileThunks.UserLogoutNotValidToken,
  async (_, { dispatch }: any) => {
    dispatch(UserSliceActions.logoutSuccess());
    localStorage.removeItem('token');
    localStorage.removeItem('tokenError');
  }
);
